import {useState} from 'react';
import clsx from 'clsx';
import type {LayoutHeader} from '../../../types';
import {Link, useUrl, useNavigate} from '@shopify/hydrogen';
import ElLink from '../../elements/Link';
import NewsletterForm from '../contactsNavGroup/NewsletterForm.client';
import {IoSearchOutline, IoChevronDownOutline} from 'react-icons/io5';
import ProductsCategories from './ProductCategories.client';

type Props = {
  data: LayoutHeader;
  onClose: () => void;
};

export default function MenuNavGroupContentNew({data, onClose}: Props) {
  const url = useUrl();
  const pathname = url.pathname;
  const [currentUrl, setCurrentUrl] = useState('');

  const [isNewsletterActive, setIsNewsletterActive] = useState<Boolean>(false);
  const [isSearchActive, setIsSearchActive] = useState<Boolean>(false);
  const [isContactsActive, setIsContactsActive] = useState<Boolean>(false);

  const [workType, setWorkType] = useState<string>('');

  const handleWorkType = (str: string) => {
    setWorkType(str);
  };

  const handleReset = () => {
    setIsContactsActive(false);
    setIsNewsletterActive(false);
    setIsSearchActive(false);
  };

  const handleNewsletter = () => {
    setIsContactsActive(false);
    setIsSearchActive(false);
    setWorkType('');
    setIsNewsletterActive(!isNewsletterActive);
  };

  const handleSearch = () => {
    setIsContactsActive(false);
    setIsNewsletterActive(false);
    setWorkType('');
    setIsSearchActive(!isSearchActive);
  };

  const handleContacts = () => {
    setIsSearchActive(false);
    setIsNewsletterActive(false);
    setWorkType('');
    setIsContactsActive(!isContactsActive);
  };

  const navigate = useNavigate();
  const [path, setPath] = useState<string>('');

  const handleValue = (e: any) => {
    setPath(e.target.value);
  };

  const handlePath = (str: string) => {
    return '/search/' + str;
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
  };

  const handleEnter = (e: any) => {
    if (e.keyCode === 13 && path.length >= 3) {
      navigate(handlePath(path), {replace: true});
      onClose();
    }
  };

  // if (typeof window !== 'undefined') {
  //   function updateUrl() {
  //     setCurrentUrl(window.location.href);
  //   }
  //   setInterval(updateUrl, 10);
  // }

  const contacts = data?.contacts;
  const productCategories = data?.productCategories;

  return (
    <div className="max-h-[calc(100vh-100px)] overflow-y-scroll pt-headerSM">
      <div className="grid gap-y-20 lg:grid-cols-8 lg:gap-5">
        <div className="lg:col-span-2">
          <Link
            to="/artists"
            className={clsx(
              'link--nav',
              pathname.includes('/artists') &&
                !pathname.includes('/products') &&
                'text-main',
            )}
            onClick={onClose}
          >
            Artists
          </Link>
          <Link
            to="/exhibitions"
            className={clsx(
              'link--nav',
              pathname.includes('/exhibitions') && 'text-main',
            )}
            onClick={onClose}
          >
            Exhibitions
          </Link>
        </div>
        <div className="lg:col-span-4">
          {productCategories.map((cat) =>
            cat.links.length > 1 ? (
              <div key={cat._key}>
                <ProductsCategories
                  category={cat}
                  handleWorkType={handleWorkType}
                  workType={workType}
                  onClose={onClose}
                  handleReset={handleReset}
                />
              </div>
            ) : (
              <div key={cat._key}>
                {cat.links.map((link) => (
                  <Link
                    key={link.slug}
                    to={link.slug}
                    onClick={onClose}
                    className="link--nav"
                  >
                    {link.title}{' '}
                    <span className="ml-fontSpace !capitalize text-main">
                      {link.tag}
                    </span>
                  </Link>
                ))}
              </div>
            ),
          )}
        </div>

        <div className="lg:hidden">
          <button
            className={clsx(
              'link--nav flex cursor-pointer',
              isSearchActive && 'text-main',
            )}
            onClick={handleSearch}
          >
            <IoSearchOutline className="mr-2" size={16} />
            Search
          </button>
          {isSearchActive && (
            <form
              className="relative"
              onSubmit={handleSubmit}
              onKeyDown={handleEnter}
            >
              <input
                type="text"
                placeholder="Type anything…"
                value={path}
                onChange={handleValue}
                className="mt-20 w-full text-small text-black placeholder:text-gray"
              />
              <div className='h-[1px] bg-gray w-full mt-2 mb-10 w-full' />
              <div className="mt-20">
                <button
                  className="w-fit border border-main px-10 py-2 text-main hover:bg-main hover:text-white disabled:cursor-not-allowed disabled:border-gray disabled:text-gray disabled:hover:bg-white"
                  disabled={path.length < 3 ? true : false}
                >
                  {path.length >= 3 ? (
                    <Link to={handlePath(path)} onClick={onClose}>
                      Search
                    </Link>
                  ) : (
                    <p>Search</p>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="lg:col-span-1">
          <Link
            to="/pages/about"
            className={clsx(
              'link--nav',
              pathname.includes('/pages/about') && 'text-main',
            )}
            onClick={onClose}
          >
            About
          </Link>
          <div>
            <button
              className={clsx(
                'link--nav flex cursor-pointer',
                isNewsletterActive && 'text-main',
              )}
              onClick={handleNewsletter}
            >
              Newsletter{' '}
              <span className="inline">
                <IoChevronDownOutline
                  className={clsx(
                    'ml-fontSpace',
                    isNewsletterActive ? 'rotate-180' : '',
                  )}
                  size={16}
                />
              </span>
            </button>
            {isNewsletterActive && <NewsletterForm />}
          </div>
        </div>

        <div className="lg:col-span-1">
          <Link
            to="/account"
            className={clsx(
              'link--nav',
              currentUrl.includes('/account') && 'text-main',
            )}
            onClick={onClose}
          >
            Account
          </Link>
          <button
            className={clsx(
              'link--nav flex cursor-pointer',
              isContactsActive && 'text-main',
            )}
            onClick={handleContacts}
          >
            Contacts{' '}
            <span className="inline">
              <IoChevronDownOutline
                className={clsx(
                  'ml-fontSpace',
                  isContactsActive ? 'rotate-180' : '',
                )}
                size={16}
              />
            </span>
          </button>
          {isContactsActive && (
            <div className="mt-20">
              {contacts.email && (
                <a
                  href={`mailto:${contacts.email}`}
                  className="text-gray hover:text-main"
                >
                  {contacts.email}
                </a>
              )}
              {contacts.instagram && (
                <ElLink
                  link={contacts.instagram}
                  children={<>{contacts.instagram.title}</>}
                  className="text-gray hover:text-main"
                />
              )}
              {contacts.tsbInstagram && (
                <ElLink
                  link={contacts.tsbInstagram}
                  children={<>{contacts.tsbInstagram.title}</>}
                  className="text-gray hover:text-main"
                />
              )}
              {contacts.address && (
                <div className="mt-20">
                  <p className="text-small">{contacts.address.title}</p>
                  <a
                    href={contacts.address.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-pre-line text-gray hover:text-main"
                  >
                    {contacts.address.text}
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
